import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { InlineImageNodeView } from './inline-image-node-view'

export interface InlineImageOptions {
  HTMLAttributes: Record<string, any>
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    inlineImage: {
      /**
       * Insert an inline image
       */
      insertInlineImage: (options: {
        src: string
        alt?: string
        title?: string
        darkSrc?: string
        display?: 'inline' | 'original' | 'block'
      }) => ReturnType
      /**
       * Update inline image attributes
       */
      updateInlineImage: (position: number, attributes: {
        src?: string
        alt?: string
        title?: string
        darkSrc?: string
        display?: 'inline' | 'original' | 'block'
      }) => ReturnType
    }
  }
}

export const InlineImageNode = Node.create<InlineImageOptions>({
  name: 'inlineImage',

  priority: 1000,

  group: 'inline',

  inline: true,

  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  addAttributes() {
    return {
      src: {
        default: '',
        parseHTML: element => element.getAttribute('src') || '',
        renderHTML: attributes => ({
          src: attributes.src,
        }),
      },
      alt: {
        default: '',
        parseHTML: element => element.getAttribute('alt') || '',
        renderHTML: attributes => ({
          alt: attributes.alt || '',
        }),
      },
      title: {
        default: '',
        parseHTML: element => element.getAttribute('title') || '',
        renderHTML: attributes => {
          if (!attributes.title) {
            return {}
          }
          return {
            title: attributes.title,
          }
        },
      },
      darkSrc: {
        default: null,
        parseHTML: element => element.getAttribute('data-dark-src') || null,
        renderHTML: attributes => {
          if (!attributes.darkSrc) {
            return {}
          }
          return {
            'data-dark-src': attributes.darkSrc,
          }
        },
      },
      display: {
        default: 'inline',
        parseHTML: element => element.getAttribute('data-display') || 'inline',
        renderHTML: attributes => ({
          'data-display': attributes.display || 'inline',
        }),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'img[data-inline-image]',
      },
      {
        tag: 'span[data-inline-image-wrapper]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const { darkSrc, display } = HTMLAttributes
    
    // If there's a dark mode image, wrap in a span
    if (darkSrc) {
      return [
        'span',
        {
          'data-inline-image-wrapper': '',
          class: 'inline-image-wrapper',
        },
        [
          'img',
          mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
            'data-inline-image': '',
            class: `inline-image inline-image--${display || 'inline'} light-image`,
          }),
        ],
        [
          'img',
          mergeAttributes(this.options.HTMLAttributes, {
            src: darkSrc,
            alt: HTMLAttributes.alt || '',
            title: HTMLAttributes.title || '',
            'data-inline-image': '',
            class: `inline-image inline-image--${display || 'inline'} dark-image`,
          }),
        ],
      ]
    }
    
    // Single image
    return [
      'img',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-inline-image': '',
        class: `inline-image inline-image--${display || 'inline'}`,
      }),
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(InlineImageNodeView)
  },

  addCommands() {
    return {
      insertInlineImage:
        options =>
        ({ chain, state }) => {
          const { selection } = state
          const { from } = selection

          return chain()
            .insertContentAt(from, {
              type: this.name,
              attrs: {
                src: options.src,
                alt: options.alt || '',
                title: options.title || '',
                darkSrc: options.darkSrc || null,
                display: options.display || 'inline',
              },
            })
            .run()
        },
      
      updateInlineImage:
        (_position, attributes) =>
        ({ chain }) => {
          return chain()
            .updateAttributes(this.name, attributes)
            .run()
        },
    }
  },
})